import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  CalendarCheck,
  CircleDollarSign,
  FileText,
  HandCoins,
  MessageCircle,
  NotebookPen,
  Phone,
  Printer,
  Repeat,
  Undo2,
  XCircle,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type {
  CollectionEntry,
  CollectionSettlement,
} from '@aeris/contracts'
import { ZERO_AMOUNT, parseAmount } from '@aeris/finance'
import { formatMoney, subtract } from '@aeris/money'
import {
  settlementReceiptFor,
  statementFor,
  type PrintDocument,
} from '@aeris/printing'
import {
  collectionPromiseText,
  collectionTimelineOf,
  collectionTimelineText,
} from '@aeris/status-meta'
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  InlineAlert,
  LoadFailed,
  Section,
  Spinner,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  VisuallyHidden,
  cn,
  notify,
} from '@aeris/ui'

import { fetchCollectionDetail } from '../lib/api/collections.ts'
import {
  fetchSalesDocument,
  transitionSalesDocument,
} from '../lib/api/sales.ts'
import {
  ApiError,
  fetchCompany,
  fetchCurrentCashSession,
  fetchFinanceEntry,
  fetchPaymentMethods,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { statementEntriesOf } from '../lib/collection-statement.ts'
import { formatDate } from '../lib/date-format.ts'
import { formatDocument } from '../lib/document-format.ts'
import { formatPhone } from '../lib/phone-format.ts'
import { readRememberedTerminal } from '../lib/remembered-terminal.ts'
import { useReceiptTemplate } from '../lib/use-receipt-template.ts'

import { AuthorisationDialog } from './authorisation-dialog.tsx'
import { CollectionContactDialog } from './collection-contact-dialog.tsx'
import { CollectionMessageDialog } from './collection-message-dialog.tsx'
import { FinanceSettleDialog } from './finance-settle-dialog.tsx'
import { PartyCreditPanel } from './party-credit-panel.tsx'
import { PrintDocumentDialog } from './print-document-dialog.tsx'
import { RenegotiateDialog } from './renegotiate-dialog.tsx'
import { SalesDocumentDialog } from './sales-document-dialog.tsx'

/**
 * O cliente na cobrança (F135): tudo o que se precisa para decidir o que fazer
 * agora, sem abrir outra tela.
 *
 * De cima para baixo, na ordem da ligação: quem é e como falar com ele, quanto
 * deve, o que ele prometeu, os títulos (receber ou renegociar), e o que já se
 * fez — contatos, pagamentos e renegociações numa linha do tempo só.
 */

const brl = (amount: string): string => formatMoney(parseAmount(amount))

const clock = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

export const CollectionPanel = ({
  partyId,
  onBack,
}: {
  partyId: string
  /** Volta para a fila no celular, onde o painel ocupa a tela. */
  onBack: () => void
}): ReactNode => {
  const { allows, session } = useAuth()
  const queryClient = useQueryClient()

  const canCollect = allows('finance.collect')
  const canSettle = allows('finance.settle')
  const canRenegotiate = allows('finance.write')

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [dialog, setDialog] = useState<
    'contact' | 'message' | 'renegotiate' | null
  >(null)
  const [printing, setPrinting] = useState<PrintDocument | null>(null)
  const [settlingId, setSettlingId] = useState<string | null>(null)
  // A nota por trás de um título (F237): ver, imprimir e mandar ao cliente.
  const [viewingSale, setViewingSale] = useState<string | null>(null)
  const canSeeSale = allows('sales.read')
  /**
   * Cancelar a venda dali mesmo (pedido do Eduardo, 15/10): o título cai junto
   * e o limite do cliente volta (F286). Pedido com dinheiro recebido continua
   * em Vendas, que pergunta se devolve ou vira vale.
   */
  /*
   * O caixa também vê o botão (pedido do Swat, 19/10): sem `sales.cancel`, o
   * servidor responde `authorisation_required` e a senha do gerente libera —
   * o mesmo vale para pedido que segura mercadoria, até para o dono.
   */
  const canCancelSale = allows('sales.cancel') || allows('finance.settle')
  const [cancellingSale, setCancellingSale] = useState<CollectionEntry | null>(
    null
  )
  const [cancelBusy, setCancelBusy] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [authorisingSale, setAuthorisingSale] = useState<{
    entry: CollectionEntry
    salesDocumentId: string
    reason: string | null
  } | null>(null)

  const cancelSale = async (
    entry: CollectionEntry,
    reason: string | null,
    authorisationId: string | null = null
  ): Promise<void> => {
    if (entry.salesDocumentId === null) return
    const salesDocumentId = entry.salesDocumentId
    setCancelBusy(true)
    try {
      const { document } = await fetchSalesDocument(salesDocumentId)
      await transitionSalesDocument(document.id, {
        version: document.version,
        to: 'cancelled',
        reason,
        authorisationId,
      })
      setCancelError(null)
      notify.success(
        `${entry.description}: cancelado. O título saiu da conta e o limite voltou.`
      )
      await queryClient.invalidateQueries({ queryKey: ['collections'] })
    } catch (cause: unknown) {
      if (
        cause instanceof ApiError &&
        cause.code === 'authorisation_required' &&
        authorisationId === null
      ) {
        setCancelError(null)
        setAuthorisingSale({ entry, salesDocumentId, reason })
        return
      }
      setCancelError(
        cause instanceof ApiError && cause.code === 'paid_resolution_required'
          ? 'Esse pedido tem dinheiro recebido. Cancele pela tela de Vendas, que pergunta se devolve ou vira vale.'
          : cause instanceof ApiError
            ? cause.message
            : 'Não foi possível cancelar.'
      )
    } finally {
      setCancelBusy(false)
      setCancellingSale(null)
    }
  }

  const detail = useQuery({
    queryKey: ['collections', 'detail', partyId],
    queryFn: ({ signal }) => fetchCollectionDetail(partyId, signal),
  })

  const companyId = session?.context?.companyId ?? null
  const company = useQuery({
    queryKey: ['company', companyId],
    queryFn: ({ signal }) => fetchCompany(companyId ?? '', signal),
    enabled: companyId !== null && allows('organization.read'),
  })

  const methods = useQuery({
    queryKey: ['payment-methods', 'active'],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
    enabled: canSettle,
  })

  const terminalId = readRememberedTerminal()
  const branchId = session?.context?.branchId ?? undefined
  const till = useQuery({
    queryKey: ['cash-session', { terminalId, branchId }],
    queryFn: ({ signal }) =>
      fetchCurrentCashSession({ terminalId, branchId }, signal),
    enabled: canSettle && terminalId !== '',
  })

  const settling = useQuery({
    queryKey: ['finance', 'entry', settlingId],
    queryFn: ({ signal }) => fetchFinanceEntry(settlingId ?? '', signal),
    enabled: settlingId !== null,
  })

  // O modelo da nota da loja (F149): o extrato e o recibo de baixa saem com o
  // cabeçalho e o rodapé que ela escreveu, como o cupom do PDV.
  const template = useReceiptTemplate()

  if (detail.isPending) {
    return (
      <div className="flex justify-center py-10">
        <Spinner label="Carregando o cliente" />
      </div>
    )
  }
  if (detail.isError) {
    return (
      <LoadFailed
        what="o cliente"
        onRetry={() => {
          void detail.refetch()
        }}
      />
    )
  }

  const data = detail.data
  const companyData = company.data?.company
  const companyName =
    companyData?.tradeName ??
    companyData?.legalName ??
    session?.context?.companyName ??
    'a loja'
  const header = {
    name: companyName,
    document: formatDocument(companyData?.cnpj ?? null),
    addressLine:
      companyData === undefined
        ? null
        : [
            [companyData.fiscal.street, companyData.fiscal.streetNumber]
              .filter((part) => part !== null && part !== '')
              .join(', '),
            companyData.fiscal.district,
            [companyData.fiscal.city, companyData.fiscal.state]
              .filter((part) => part !== null && part !== '')
              .join('/'),
          ]
            .filter((part) => part !== null && part !== '')
            .join(' — ') || null,
  }

  const chosen = data.entries.filter((entry) => selected.has(entry.id))
  const renegotiable = (entry: CollectionEntry): boolean =>
    entry.settledAmount === '0.00'

  const toggle = (entryId: string): void => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(entryId)) next.delete(entryId)
      else next.add(entryId)
      return next
    })
  }

  const printStatement = (): void => {
    const upcoming = subtract(
      parseAmount(data.standing.outstanding),
      parseAmount(data.standing.overdueAmount)
    )
    setPrinting(
      statementFor({
        template,
        company: header,
        customer: {
          name: data.party.legalName,
          document: formatDocument(data.party.document),
        },
        asOf: new Date(),
        // Com o que o cliente levou em cada nota (F237).
        entries: statementEntriesOf(data),
        overdueTotal: parseAmount(data.standing.overdueAmount),
        upcomingTotal: upcoming,
        total: parseAmount(data.standing.outstanding),
        payments: data.settlements
          .filter(
            (settlement) => !settlement.isReversal && !settlement.reversed
          )
          .slice(0, 5)
          .map((settlement) => ({
            settledOn: new Date(`${settlement.settledOn}T12:00:00`),
            description: settlement.entryDescription,
            paid: parseAmount(settlement.paidAmount),
            method: settlement.methodName,
          })),
      })
    )
  }

  const printReceipt = (settlement: CollectionSettlement): void => {
    const entry = data.entries.find((row) => row.id === settlement.entryId)
    setPrinting(
      settlementReceiptFor({
        template,
        company: header,
        customer: {
          name: data.party.legalName,
          document: formatDocument(data.party.document),
        },
        entryDescription: settlement.entryDescription,
        settledOn: new Date(`${settlement.settledOn}T12:00:00`),
        amount: parseAmount(settlement.amount),
        interest: parseAmount(settlement.interestAmount),
        fine: parseAmount(settlement.fineAmount),
        discount: parseAmount(settlement.discountAmount),
        paid: parseAmount(settlement.paidAmount),
        method: settlement.methodName,
        receivedBy: settlement.actorName,
        remaining:
          entry === undefined ? ZERO_AMOUNT : parseAmount(entry.outstanding),
        settlementId: settlement.id,
        reversed: settlement.reversed,
      })
    )
  }

  const promise = data.promise
  const timeline = collectionTimelineOf(data)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="self-start lg:hidden"
            leadingIcon={<ArrowLeft aria-hidden="true" className="h-4 w-4" />}
            onClick={onBack}
          >
            Voltar para a fila
          </Button>
          <h2 className="truncate text-(length:--text-xl) font-(--weight-semibold) text-(--text-primary)">
            {data.party.name}
          </h2>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-(length:--text-sm) text-(--text-secondary)">
            {data.party.document === null ? null : (
              <span className="font-(--font-mono)">
                {formatDocument(data.party.document)}
              </span>
            )}
            {data.phones.map((phone) => (
              <a
                key={`${phone.kind}-${phone.value}`}
                href={`tel:+55${phone.value}`}
                className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
              >
                <Phone aria-hidden="true" className="h-3.5 w-3.5" />
                {formatPhone(phone.value)}
              </a>
            ))}
            {data.phones.length === 0 ? (
              <span className="text-(--text-muted)">sem telefone</span>
            ) : null}
            {data.standing.creditBlocked ? (
              <Badge tone="danger">Crédito bloqueado</Badge>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {canCollect ? (
            <>
              <Button
                variant="primary"
                size="sm"
                leadingIcon={
                  <MessageCircle aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setDialog('message')
                }}
              >
                WhatsApp
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={
                  <NotebookPen aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  setDialog('contact')
                }}
              >
                Registrar contato
              </Button>
            </>
          ) : null}
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<FileText aria-hidden="true" className="h-4 w-4" />}
            onClick={printStatement}
          >
            Extrato
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Em aberto" value={brl(data.standing.outstanding)} />
        <StatTile
          label="Vencido"
          value={
            <span
              className={cn(
                data.standing.overdueCount > 0 ? 'text-(--danger)' : undefined
              )}
            >
              {brl(data.standing.overdueAmount)}
            </span>
          }
          hint={
            data.standing.overdueCount === 0
              ? 'nada vencido'
              : `${String(data.standing.overdueCount)} ${data.standing.overdueCount === 1 ? 'título' : 'títulos'}`
          }
        />
        <StatTile
          label="Atraso mais antigo"
          value={
            data.standing.oldestOverdueDays === 0
              ? '—'
              : `${String(data.standing.oldestOverdueDays)} ${data.standing.oldestOverdueDays === 1 ? 'dia' : 'dias'}`
          }
        />
        <StatTile
          label="Limite de crédito"
          value={
            data.standing.creditLimit === null
              ? 'sem limite'
              : brl(data.standing.creditLimit)
          }
          hint={data.standing.creditBlockReason ?? undefined}
        />
      </div>

      {promise === null ? null : (
        <InlineAlert
          tone={
            promise.state === 'broken'
              ? 'danger'
              : promise.state === 'due_today'
                ? 'warning'
                : 'info'
          }
          title={collectionPromiseText(promise).title}
        >
          {collectionPromiseText(promise).detail}
        </InlineAlert>
      )}

      <Section
        title="Títulos em aberto"
        level={3}
        action={
          canRenegotiate ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={chosen.length === 0}
              leadingIcon={<Repeat aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setDialog('renegotiate')
              }}
            >
              {chosen.length === 0
                ? 'Renegociar'
                : `Renegociar ${String(chosen.length)}`}
            </Button>
          ) : undefined
        }
      >
        {data.entries.length === 0 ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Nada em aberto. Este cliente está em dia.
          </p>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {canRenegotiate ? (
                    <TableHeaderCell>
                      <VisuallyHidden>Marcar para renegociar</VisuallyHidden>
                    </TableHeaderCell>
                  ) : null}
                  <TableHeaderCell>Vencimento</TableHeaderCell>
                  <TableHeaderCell>Descrição</TableHeaderCell>
                  <TableHeaderCell numeric>Em aberto</TableHeaderCell>
                  {canSettle ? (
                    <TableHeaderCell>
                      <VisuallyHidden>Receber</VisuallyHidden>
                    </TableHeaderCell>
                  ) : null}
                  {canCancelSale ? (
                    <TableHeaderCell>
                      <VisuallyHidden>Cancelar</VisuallyHidden>
                    </TableHeaderCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.entries.map((entry) => (
                  <TableRow key={entry.id}>
                    {canRenegotiate ? (
                      <TableCell>
                        <Checkbox
                          aria-label={`Renegociar ${entry.description}`}
                          checked={selected.has(entry.id)}
                          disabled={!renegotiable(entry)}
                          onCheckedChange={() => {
                            toggle(entry.id)
                          }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="whitespace-nowrap">
                      <span className="flex flex-col">
                        {formatDate(entry.dueAt)}
                        {entry.daysOverdue > 0 ? (
                          <span className="text-(length:--text-xs) text-(--danger)">
                            {`${String(entry.daysOverdue)} ${entry.daysOverdue === 1 ? 'dia' : 'dias'} de atraso`}
                          </span>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell className="text-(--text-primary)">
                      {entry.salesDocumentId !== null && canSeeSale ? (
                        <button
                          type="button"
                          className="text-left underline-offset-2 hover:underline"
                          title="Ver a nota: o que o cliente levou"
                          onClick={() => {
                            setViewingSale(entry.salesDocumentId)
                          }}
                        >
                          {entry.description}
                        </button>
                      ) : (
                        entry.description
                      )}
                      {renegotiable(entry) ? null : (
                        <Badge tone="neutral" className="ml-2">
                          pago em parte
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell numeric>{brl(entry.outstanding)}</TableCell>
                    {canSettle ? (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          leadingIcon={
                            <HandCoins
                              aria-hidden="true"
                              className="h-3.5 w-3.5"
                            />
                          }
                          onClick={() => {
                            setSettlingId(entry.id)
                          }}
                        >
                          Receber
                        </Button>
                      </TableCell>
                    ) : null}
                    {canCancelSale ? (
                      <TableCell>
                        {entry.salesDocumentId === null ? null : (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Cancelar ${entry.description}`}
                            leadingIcon={
                              <XCircle
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                            onClick={() => {
                              setCancellingSale(entry)
                            }}
                          >
                            Cancelar
                          </Button>
                        )}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {cancelError === null ? null : (
          <InlineAlert tone="danger">{cancelError}</InlineAlert>
        )}
        {canRenegotiate &&
        data.entries.some((entry) => !renegotiable(entry)) ? (
          <p className="text-(length:--text-xs) text-(--text-muted)">
            Título pago em parte não entra na renegociação: receba o restante
            dele, ou renegocie só os outros.
          </p>
        ) : null}
      </Section>

      {allows('finance.credit.manage') || allows('finance.read') ? (
        <PartyCreditPanel
          partyId={partyId}
          canManage={allows('finance.credit.manage')}
        />
      ) : null}

      <Section title="Linha do tempo" level={3}>
        {timeline.length === 0 ? (
          <p className="text-(length:--text-sm) text-(--text-muted)">
            Nenhum contato, pagamento ou renegociação registrado ainda.
          </p>
        ) : (
          <ol className="flex flex-col gap-3">
            {timeline.map((item) => (
              <li key={`${item.kind}-${item.id}`} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                    item.kind === 'settlement'
                      ? 'bg-(--success-subtle) text-(--success)'
                      : item.kind === 'renegotiation'
                        ? 'bg-(--warning-subtle) text-(--warning)'
                        : 'bg-(--brand-primary-subtle) text-(--brand-primary)'
                  )}
                >
                  {item.kind === 'settlement' ? (
                    item.settlement.isReversal ? (
                      <Undo2 className="h-3.5 w-3.5" />
                    ) : (
                      <CircleDollarSign className="h-3.5 w-3.5" />
                    )
                  ) : item.kind === 'renegotiation' ? (
                    <Repeat className="h-3.5 w-3.5" />
                  ) : item.contact.outcome === 'promised' ? (
                    <CalendarCheck className="h-3.5 w-3.5" />
                  ) : (
                    <MessageCircle className="h-3.5 w-3.5" />
                  )}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex flex-wrap items-baseline justify-between gap-2 text-(length:--text-sm) text-(--text-primary)">
                    <span className="font-(--weight-medium)">
                      {collectionTimelineText(item).title}
                    </span>
                    <span className="text-(length:--text-xs) text-(--text-muted)">
                      {clock.format(new Date(item.at))}
                    </span>
                  </span>
                  <span className="text-(length:--text-xs) text-(--text-secondary)">
                    {collectionTimelineText(item).detail}
                  </span>
                  {item.kind === 'settlement' && !item.settlement.isReversal ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="self-start"
                      leadingIcon={
                        <Printer aria-hidden="true" className="h-3.5 w-3.5" />
                      }
                      onClick={() => {
                        printReceipt(item.settlement)
                      }}
                    >
                      Recibo
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Section>

      {dialog === 'contact' ? (
        <CollectionContactDialog
          partyId={partyId}
          customerName={data.party.name}
          suggestedAmount={
            data.standing.overdueAmount === '0.00'
              ? data.standing.outstanding
              : data.standing.overdueAmount
          }
          onClose={() => {
            setDialog(null)
          }}
        />
      ) : null}

      {dialog === 'message' ? (
        <CollectionMessageDialog
          detail={data}
          companyName={companyName}
          onClose={() => {
            setDialog(null)
          }}
        />
      ) : null}

      {dialog === 'renegotiate' && chosen.length > 0 ? (
        <RenegotiateDialog
          partyId={partyId}
          customerName={data.party.name}
          entries={chosen}
          onClose={() => {
            setDialog(null)
            setSelected(new Set())
          }}
        />
      ) : null}

      {viewingSale === null ? null : (
        <SalesDocumentDialog
          documentId={viewingSale}
          onClose={() => {
            setViewingSale(null)
          }}
        />
      )}

      <ConfirmDialog
        open={cancellingSale !== null}
        onOpenChange={(next) => {
          if (!next) setCancellingSale(null)
        }}
        title="Cancelar a venda"
        description={
          cancellingSale === null
            ? ''
            : `Cancelar "${cancellingSale.description}"? O título sai da conta do cliente, o limite volta e a mercadoria volta ao estoque. Fica no histórico com o motivo.`
        }
        confirmLabel="Cancelar a venda"
        cancelLabel="Voltar"
        reason={{
          label: 'Motivo',
          placeholder: 'Ex.: cliente devolveu',
          required: true,
        }}
        loading={cancelBusy}
        onConfirm={(reason) => {
          if (cancellingSale === null) return
          void cancelSale(cancellingSale, reason?.trim() ?? null)
        }}
      />

      {authorisingSale === null ? null : (
        <AuthorisationDialog
          title={`Cancelar "${authorisingSale.entry.description}"`}
          description="Um supervisor precisa autorizar o cancelamento. O título sai da conta e a mercadoria volta ao estoque."
          action="sales.cancel"
          targetType="sales_document"
          targetId={authorisingSale.salesDocumentId}
          onAuthorised={(authorisationId, reason) => {
            const { entry } = authorisingSale
            const why = authorisingSale.reason ?? reason
            setAuthorisingSale(null)
            void cancelSale(entry, why, authorisationId)
          }}
          onClose={() => {
            setAuthorisingSale(null)
          }}
        />
      )}

      {printing === null ? null : (
        <PrintDocumentDialog
          document={printing}
          initialPaper={printing.kind === 'report' ? 'a4' : '48'}
          onClose={() => {
            setPrinting(null)
          }}
        />
      )}

      {settlingId !== null && settling.data !== undefined ? (
        <FinanceSettleDialog
          key={settlingId}
          entry={settling.data.entry}
          methods={methods.data?.methods ?? []}
          cashSessionId={till.data?.session?.id ?? null}
          onClose={() => {
            setSettlingId(null)
            void queryClient.invalidateQueries({ queryKey: ['collections'] })
          }}
        />
      ) : null}
    </div>
  )
}
